import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import dayjs from 'dayjs';
import { QuickFillApi } from '@frontend/api-quick-fill';
import { ScheduleQueries } from '@frontend/api-schedule';
import { useTranslation } from '@frontend/i18n';
import { useQuery } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { useAlert } from '@frontend/design-system';
import { WaitlistSlotInfo } from '../components/waitlist-appointment-details-step/use-waitlist-appointment-details-form';
import { DATETIME_FORMATS } from '../constants';
import { useWaitlistShallowStore } from '../stores';

const formatTemplateForUpdateOnly = (template: string) => {
  // Handle specific dynamic field key mappings to return the correct key content and format for the waitlist api - single braces, lowercase, and different text.
  return template.replaceAll(/\{([^{}]+)\}/g, (_group, content) => {
    const upperCaseContent = content.toUpperCase();
    if (upperCaseContent === DynamicFieldKey_Enum.BUSINESS_GROUP_NAME) {
      return 'practice_name';
    }
    if (upperCaseContent === DynamicFieldKey_Enum.DATETIME_FORMAL) {
      return 'date_time';
    }
    if (upperCaseContent === DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE) {
      return 'practice_phone';
    }
    return content.toLowerCase();
  });
};

const getTemplateWithAppointmentSlots = (
  originalTemplate: string,
  slots: WaitlistSlotInfo[],
  locationTimezone: string
) => {
  if (slots.length === 1) return originalTemplate;

  const dateTimeList = slots
    .map((slot, idx) => {
      const number = idx + 1;
      if (idx === 0) {
        return `${number}. {date_time}`; // First slot uses template tag as backend will insert the first date here
      }
      const formattedDate = dayjs(slot.startDateTime).tz(locationTimezone).format('ddd, MMMM D');
      const formattedTime = dayjs(slot.startDateTime).tz(locationTimezone).format('h:mmA');
      return `${number}. ${formattedTime} on ${formattedDate} `;
    })
    .join('\n');

  return originalTemplate.replace('{date_time}', `\n${dateTimeList}`);
};

const useGetManualTemplate = (locationId: string) => {
  const { data: manualTemplateData, refetch } = useQuery({
    queryKey: ['scheduleQuickFillListTemplate', locationId],
    queryFn: () => QuickFillApi.getDefaultTemplate(locationId),
    enabled: !!locationId,
  });

  const manualTemplateString = manualTemplateData?.Template || '';

  return { manualTemplateString, refetchManualTemplateString: refetch };
};

// NOTE: this function used for non-direct scheduling case of waitlist messaging
// in which we just need to send messages without scheduling any appointments.
export const useSendWaitlistMessage = () => {
  const { t } = useTranslation('schedule-quick-fill');
  const alert = useAlert();
  const { selectedLocationId, selectedPersonIds, selectedTemplateString, resetUserSelections } =
    useWaitlistShallowStore('selectedLocationId', 'selectedPersonIds', 'selectedTemplateString', 'resetUserSelections');
  const { manualTemplateString, refetchManualTemplateString } = useGetManualTemplate(selectedLocationId || '');
  const sendQuickFillMessageMutation = ScheduleQueries.useSendQuickFillMessage();
  // received time wise sorted array of appointment slots
  const handleSendMessage = async (slots: WaitlistSlotInfo[], locationTimezone: string) => {
    if (!selectedLocationId || selectedPersonIds.length === 0) return;

    const sortedSlots = slots.sort((a, b) =>
      a.startDateTime === b.startDateTime ? 0 : a.startDateTime > b.startDateTime ? 1 : -1
    );

    const formattedTemplate = formatTemplateForUpdateOnly(selectedTemplateString);
    const templateWithSlots = getTemplateWithAppointmentSlots(formattedTemplate, sortedSlots, locationTimezone);

    const isUpdateTemplate = manualTemplateString !== templateWithSlots;

    if (isUpdateTemplate) {
      await QuickFillApi.updateTemplate(templateWithSlots, selectedLocationId);
    }
    // send message
    try {
      const firstSlotDateTime = dayjs
        .tz(sortedSlots[0].startDateTime)
        .format(DATETIME_FORMATS.ISO_DATETIME_WITH_MILLISECONDS_UTC);

      await sendQuickFillMessageMutation.mutateAsync({
        locationId: selectedLocationId,
        personIds: selectedPersonIds,
        startTime: firstSlotDateTime,
        endTime: firstSlotDateTime, // End time is mandatory for the API, but not used in the message anymore
      });
      alert.success(t('Waitlist message sent successfully'));
      resetUserSelections();
    } catch (error) {
      console.error('Error sending Waitlist message:', error);
      alert.error(t('Failed to send Waitlist message'));
      sentry.error({ team: 'schedule', topic: 'scheduling', error });
    }

    if (isUpdateTemplate) {
      await refetchManualTemplateString();
    }
  };

  return { handleSendMessage };
};
